import React, { useEffect } from 'react';
import {
    applyConditionToAllItems,
    applyDefaultUsableDecisions,
    getHandoverInventoryItemId,
    hasKnownQty,
    needsInventoryDecision,
    setHandoverDamagedQty,
    setHandoverItemCondition,
} from './handoverInventoryDecisions';

const HandoverInventoryDecisionFields = ({ items: allItems = [], value = {}, onChange, dark = false, embedded = false }) => {
    // Only lines OATRx holds stock for get a decision; the rest are usable.
    const items = allItems.filter(needsInventoryDecision);

    useEffect(() => {
        const updatedValue = applyDefaultUsableDecisions(items, value);
        if (updatedValue !== value) {
            onChange(updatedValue);
        }
    }, [items, onChange, value]);

    const setCondition = (item, condition) => {
        const itemId = getHandoverInventoryItemId(item);
        if (!itemId) return;

        onChange({
            ...value,
            [itemId]: setHandoverItemCondition(item, value[itemId] || {}, condition),
        });
    };

    const setDamaged = (item, nextValue) => {
        const itemId = getHandoverInventoryItemId(item);
        if (!itemId) return;

        onChange({
            ...value,
            [itemId]: setHandoverDamagedQty(item, value[itemId] || {}, nextValue),
        });
    };

    const allUsable = items.length > 0 && items.every((item) => {
        const itemId = getHandoverInventoryItemId(item);
        return ((itemId && value[itemId]) || {}).condition !== 'damaged'
            && ((itemId && value[itemId]) || {}).condition !== 'split';
    });

    const panelClass = dark
        ? 'border-white/10 bg-white/5 text-white'
        : 'border-slate-200 bg-slate-50 text-slate-900';
    const labelClass = dark ? 'text-slate-300' : 'text-slate-600';
    const fieldClass = dark
        ? 'border-slate-300 bg-white text-slate-900'
        : 'border-slate-300 bg-white text-slate-900';
    const darkFieldStyle = dark ? { backgroundColor: '#ffffff', color: '#0f172a' } : undefined;
    if (items.length === 0) {
        return null;
    }

    return (
        <div className={embedded ? 'space-y-2' : 'mt-4 space-y-2.5'}>
            {!embedded ? <div>
                <h4 className={`text-sm font-semibold ${dark ? 'text-white' : 'text-slate-900'}`}>Inventory decision</h4>
                <p className={`text-xs ${labelClass}`}>Everything is usable unless you say otherwise. Change a line only if some or all of it is damaged.</p>
            </div> : null}

            {items.length > 1 ? (
                <div className="flex flex-wrap items-center gap-2">
                    <span className={`text-xs ${labelClass}`}>All medications:</span>
                    <button
                        className={`rounded-md border px-2 py-1 text-xs font-semibold ${allUsable ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-white text-slate-700'}`}
                        type="button"
                        onClick={() => onChange(applyConditionToAllItems(items, value, 'intact'))}
                    >
                        All usable
                    </button>
                    <button
                        className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-semibold text-slate-700"
                        type="button"
                        onClick={() => onChange(applyConditionToAllItems(items, value, 'damaged'))}
                    >
                        All unusable
                    </button>
                </div>
            ) : null}

            {items.map((item) => {
                const itemId = getHandoverInventoryItemId(item);
                const decision = (itemId ? value[itemId] : null) || {};

                return (
                    <div key={itemId || item.id} className={embedded ? '' : `rounded-xl border p-3 ${panelClass}`}>
                        <div className="flex flex-wrap items-start justify-between gap-2">
                            <div className={embedded ? 'hidden' : ''}>
                                <p className="text-sm font-semibold">{item.drug || `RX ${item.rxnumber || ''}`}</p>
                                <p className={`text-xs ${labelClass}`}>DIN {item.din || '—'} · Qty {item.qty ?? '—'}</p>
                            </div>
                        </div>

                        <div className={`${embedded ? 'mt-1' : 'mt-3'} flex flex-wrap items-end gap-2`}>
                            <label className={`text-xs font-semibold ${embedded ? 'w-[180px] max-w-full' : 'w-full sm:w-[220px]'} ${labelClass}`}>
                                <span className="sr-only">Condition</span>
                                <select
                                    className={`h-10 w-full rounded-lg border px-2 text-sm outline-none ${fieldClass}`}
                                    style={darkFieldStyle}
                                    value={decision.condition || 'intact'}
                                    onChange={(event) => setCondition(item, event.target.value)}
                                >
                                    <option className="bg-white text-slate-900" value="intact">Usable</option>
                                    <option className="bg-white text-slate-900" value="damaged">Unusable</option>
                                    {hasKnownQty(item) ? (
                                        <option className="bg-white text-slate-900" value="split">Some damaged…</option>
                                    ) : null}
                                </select>
                            </label>
                            {decision.condition === 'split' ? (
                                <label className={`text-xs font-semibold ${labelClass}`}>
                                    Damaged units
                                    <input
                                        aria-label={`Damaged units for ${item.drug || `RX ${item.rxnumber || ''}`}`}
                                        className={`mt-0.5 h-10 w-[110px] rounded-lg border px-2 text-sm outline-none ${fieldClass}`}
                                        max={item.qty}
                                        min="0"
                                        style={darkFieldStyle}
                                        type="number"
                                        value={decision.damaged_qty ?? ''}
                                        onChange={(event) => setDamaged(item, event.target.value)}
                                    />
                                </label>
                            ) : null}
                            {decision.condition === 'split' && decision.damaged_qty !== '' && decision.damaged_qty !== undefined ? (
                                <span className={`pb-2 text-xs ${labelClass}`}>
                                    {decision.good_qty} good back to shelf
                                </span>
                            ) : null}
                        </div>

                    </div>
                );
            })}
        </div>
    );
};

export default HandoverInventoryDecisionFields;
